// PWA install controller — pure logic, no DOM. Decides whether to offer the native install
// prompt (Chromium), show the iOS "เพิ่มไปยังหน้าจอโฮม" steps, or a generic hint, and remembers a
// 7-day snooze when the viewer taps "ภายหลัง". Ported from the SkynetCore PWA playbook.

export type InstallState =
  | "installed"
  | "chromium_ready"
  | "ios_guidance"
  | "browser"
  | "install_accepted"
  | "install_dismissed"
  | "install_failed"
  | "snoozed";

export type InstallGuidance = "ios_safari" | "browser";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallEnv {
  standalone: boolean;
  ios: boolean;
  safari: boolean;
}

export interface InstallHandlers {
  onState?: (s: { state: InstallState; installed: boolean; environment: InstallEnv }) => void;
  onBanner?: () => void;
  onDismiss?: () => void;
  onInstructions?: (kind: InstallGuidance) => void;
  now?: () => number;
}

const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = "fs.pwa.installSnoozeUntil";

export function detectEnv(win: Window = window): InstallEnv {
  const nav = win.navigator;
  const ua = nav.userAgent || "";
  const standalone =
    !!(win.matchMedia && win.matchMedia("(display-mode: standalone)").matches) ||
    (nav as Navigator & { standalone?: boolean }).standalone === true;
  // iPadOS 13+ reports as "MacIntel" with touch points.
  const ios = /iPad|iPhone|iPod/.test(ua) || (nav.platform === "MacIntel" && (nav.maxTouchPoints || 0) > 1);
  const safari = ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return { standalone, ios, safari };
}

export interface InstallController {
  initial(): void;
  beforeInstallPrompt(e: Event): void;
  install(): Promise<{ outcome: string }>;
  dismiss(): void;
  appInstalled(): void;
}

export function createInstallController(handlers: InstallHandlers = {}, win: Window = window): InstallController {
  const env = detectEnv(win);
  let deferred: BeforeInstallPromptEvent | null = null;
  let installed = env.standalone;
  const now = (): number => (handlers.now ? handlers.now() : Date.now());
  const emit = (state: InstallState): void => handlers.onState?.({ state, installed, environment: env });
  const snoozed = (): boolean => {
    try {
      return Number((win.localStorage && win.localStorage.getItem(STORAGE_KEY)) || 0) > now();
    } catch {
      return false;
    }
  };
  const guidance = (): void => handlers.onInstructions?.(env.safari ? "ios_safari" : "browser");

  return {
    initial(): void {
      if (installed) return emit("installed");
      emit(env.safari ? "ios_guidance" : "browser");
      if (env.safari && !snoozed()) handlers.onBanner?.();
    },
    beforeInstallPrompt(e: Event): void {
      if (installed) return;
      e.preventDefault();
      deferred = e as BeforeInstallPromptEvent;
      emit("chromium_ready");
      if (!snoozed()) handlers.onBanner?.();
    },
    async install(): Promise<{ outcome: string }> {
      if (installed) return { outcome: "installed" };
      if (!deferred) {
        guidance();
        return { outcome: "instructions" };
      }
      const prompt = deferred;
      deferred = null;
      try {
        await prompt.prompt();
        const choice = await prompt.userChoice;
        emit(choice.outcome === "accepted" ? "install_accepted" : "install_dismissed");
        return choice;
      } catch {
        emit("install_failed");
        return { outcome: "failed" };
      }
    },
    dismiss(): void {
      try {
        win.localStorage?.setItem(STORAGE_KEY, String(now() + SNOOZE_MS));
      } catch { /* private mode */ }
      handlers.onDismiss?.();
      emit("snoozed");
    },
    appInstalled(): void {
      installed = true;
      deferred = null;
      handlers.onDismiss?.();
      emit("installed");
    },
  };
}
